import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
export const DISCOUNT_RATE = 0.1;

function computeTotals(items) {
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const units = items.reduce((s, i) => s + i.qty, 0);
  const discount =
    units >= 2 ? Math.round(Math.min(...items.map((i) => i.price)) * DISCOUNT_RATE) : 0;
  return { subtotal, units, discount, total: subtotal - discount };
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("mm_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("mm_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, size, qty = 1) => {
    const key = `${product.id}|${size || ""}`;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...prev,
        {
          key,
          productId: product.id,
          name: product.name,
          brand: product.brand,
          price: product.price,
          image: product.image,
          size: size || null,
          qty,
        },
      ];
    });
    toast.success("Agregado al carrito", {
      description: product.name,
      action: { label: "Ver carrito", onClick: () => setOpen(true) },
    });
  };

  const updateQty = (key, delta) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0)
    );
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const totals = useMemo(() => computeTotals(items), [items]);

  return (
    <CartContext.Provider
      value={{ items, open, setOpen, addItem, updateQty, removeItem, clear, totals }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
